/**
 * A file chosen for a message, before it is a row (#1069 Story 4.1, Task 1).
 *
 * ⚠️ THIS FILE HAS NO `.web`/`.native` SIBLINGS, AND THAT IS DELIBERATE. The
 * bundler (and Jest's react-native preset) resolve `./x` to `x.native.ts`
 * whenever that file exists, so a module that sits beside platform variants can
 * never be imported for its own sake: a test importing `./messageFilePicker`
 * gets the NATIVE one. The shared, pure contract therefore lives here under a
 * name nothing shadows. Found the hard way when 18 green tests went red the
 * moment the native picker was added.
 *
 * ⚠️ A STAGED FILE IS NOT YET A ROW. Nothing here writes anything. A file
 * chosen in the composer sits in component state until Post sends it inside the
 * message's own POST, because there is no upload-ahead endpoint (C1) and a file
 * cannot exist without the message that owns it. That is half of why OQ11
 * closed the way it did.
 *
 * ⚠️ THE BYTES TRAVEL AS BASE64, BARE. No `data:` prefix: the server's
 * `checkFileContent` does `Buffer.from(value, 'base64')`, and a prefix would
 * decode to rubbish, which is a content check passing on the wrong bytes.
 */

import { decodedBase64ByteLength } from './messageFileBytes';

/** A file the person has chosen but not yet sent. */
export interface StagedFile {
  /**
   * A client-side key, unique per staged file. NOT the row id: the row does
   * not exist until the message is posted. Two files of the same name must
   * both stage and both be removable, so the name cannot be the key.
   */
  id: string;
  /** As the platform reported it. Never trusted for type (the server sniffs). */
  name: string;
  /** The platform's CLAIM about the type. The server decides the real one. */
  mimeType: string;
  /**
   * DECODED bytes: what the person sees in their file browser, which is what
   * D1a says the 25 MB ceiling measures.
   */
  sizeBytes: number;
  /**
   * The length of the raw base64 string. D1a's SECOND check: whitespace is
   * stripped before `sizeBytes` is computed, so this is the only number that
   * notices a payload padded to smuggle bytes past the decoded ceiling.
   */
  rawChars: number;
  /** Bare base64, no `data:` prefix. */
  base64: string;
  /** Images only, for the 56x56 staging tile. Absent for everything else. */
  previewUri?: string;
  /**
   * Present, and `true`, only on an image the EDITOR staged (#1530): it is
   * referenced from the message HTML as `correspondence-inline://<id>`, is
   * hidden from the staging strip (the picture is in the box), is pruned when
   * the HTML stops referring to it, and is sent only while it is referred to.
   * It counts toward the same 6-file / 40 MB ceilings as a strip file.
   */
  inline?: true;
  /**
   * Inline only: whether the editor has EMITTED HTML that refers to this file
   * yet (#1530). The native editor inserts through an async bridge, so the
   * first HTML change after a resolve can arrive BEFORE the image is in it; a
   * file is prunable only once it has been seen in the HTML and then dropped
   * from it. Never on a strip file.
   */
  seen?: true;
}

/** The `src` the editor inserts for a staged inline image (#1530). */
export const INLINE_IMAGE_SCHEME = 'correspondence-inline';
export const inlineImageReference = (stagedId: string): string =>
  `${INLINE_IMAGE_SCHEME}://${stagedId}`;

/**
 * A file a message ALREADY holds, in the shape the ceilings read (#1530).
 *
 * An edit's ceilings are judged over held files plus staged ones, so the held
 * ones have to be countable by `admitFiles`, which reads `sizeBytes` and
 * `rawChars` only. The bytes are not here (the server has them). The id is
 * prefixed so it can never collide with a staged file's.
 *
 * ⚠️ `rawChars` IS ZERO, NOT 4/3 OF THE SIZE (review 2026-10-01). The
 * raw-payload ceilings measure what is ON THE WIRE in THIS request, and a
 * held file is not re-sent: the server's `findMessageFileLimitBreach` counts
 * held files toward the count and the decoded total only, and runs the raw
 * rules over the incoming files alone. A client that charged held bytes to
 * the raw total refused an edit the server would accept (39 MB held plus a
 * small padded image), which is a client that refuses MORE, the drift the
 * parity test in `messageFileBytes.test.ts` exists to catch.
 */
export const stagedFileFromHeld = (held: {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
}): StagedFile => ({
  id: `held-${held.id}`,
  name: held.name,
  mimeType: held.mimeType,
  sizeBytes: held.sizeBytes,
  rawChars: 0,
  base64: '',
});

/** Whether a MIME type earns a preview tile rather than a document chip. */
export const isPreviewableImage = (mimeType: string): boolean =>
  mimeType.startsWith('image/');

/** The type to record when the platform will not say. */
export const FALLBACK_MIME_TYPE = 'application/octet-stream';

/** Unique per staged file, including two files sharing a name. */
let stagedFileSeq = 0;
export const nextStagedFileId = (): string =>
  `staged-${Date.now().toString(36)}-${(stagedFileSeq += 1)}`;

/**
 * The one place a `StagedFile` is built, shared by both platforms.
 *
 * ⚠️ PURE, AND DELIBERATELY SO. Everything that decides what the composer sees
 * (the decoded size D1a measures against, the raw-character count its second
 * check needs, whether this draws as a tile or a chip, the fallback type) is
 * here and testable in the `client` project's node environment. What is left in
 * the platform files is the dialog plumbing: an `<input>` on web, three picker
 * SDKs on native. That split is what lets the contract have tests at all, since
 * no test in `src/` runs against a real DOM.
 *
 * `base64` must arrive BARE. Web strips the `data:` prefix its FileReader
 * produces before calling this; native gets bare base64 from `RNFS.readFile`.
 */
export const toStagedFile = (input: {
  name: string;
  mimeType?: string | null;
  base64: string;
}): StagedFile => {
  const base64 = input.base64;
  // The platform leaves the type empty for extensions it does not know. The
  // server sniffs the content regardless, so this only decides which shape the
  // staging strip draws.
  const mimeType = input.mimeType || FALLBACK_MIME_TYPE;
  return {
    id: nextStagedFileId(),
    name: input.name,
    mimeType,
    sizeBytes: decodedBase64ByteLength(base64),
    rawChars: base64.length,
    base64,
    // Built from the value already in hand rather than an object URL, which
    // would need revoking and would leak if the composer unmounted while files
    // were staged.
    ...(isPreviewableImage(mimeType)
      ? { previewUri: `data:${mimeType};base64,${base64}` }
      : {}),
  };
};

/**
 * What to do with a batch of file reads, some of which may have failed
 * (#1069 Story 4.1, code review 2026-09-13, patch 3).
 *
 * ⚠️ PURE, AND HERE RATHER THAN IN THE WEB PICKER, FOR THE REASON THAT FILE'S
 * HEADER GIVES: there is no DOM and no `FileReader` in the `client` Jest
 * project (probed, 2026-09-13), so a decision made inside the picker is a
 * decision no test can reach. The picker keeps the `FileReader` wiring; this
 * keeps the rule.
 *
 * ⚠️ THE RULE IS "KEEP WHAT COULD BE READ", NOT "ALL OR NOTHING". The web
 * picker used `Promise.all(...).catch(() => resolve([]))`, so one unreadable
 * file — one on a network share, one on a drive that was pulled out — dropped
 * the ENTIRE selection and resolved empty, which the panel reads as a user
 * cancel and reports with silence. Three chosen photos vanished without a
 * word: AC 4's forbidden silent truncation, at 100%.
 *
 * ⚠️ AND A PARTIAL FAILURE SPEAKS TOO (code review run 2, 2026-09-13). The
 * first version returned `{ error: null }` the moment ONE file was readable, so
 * picking five photos with two on a disconnected share staged three and dropped
 * two in silence — which is the same AC 4 violation this function was written
 * to end, merely smaller. Worse, its test asserted that behaviour, so no guard
 * could catch it. The rule is: every file that was lost is named, however many
 * survived.
 */
/**
 * An error whose message is the PRODUCT'S, safe to show a person.
 *
 * ⚠️ THE PANEL SHOWS `e.message` AND NOTHING SAID WHICH `e` (code review run 3,
 * 2026-09-13). `CorrespondencePanel`'s pick catch rendered any `Error` that
 * escaped the block, so `launchImageLibrary`'s `errorMessage` and an `RNFS`
 * failure both reached the composer verbatim — `ENOENT: no such file or
 * directory, open '/data/user/0/com.vertiv.its/cache/…jpg'` in product copy,
 * and a coding bug inside the try as `undefined is not a function`. Run 2 was
 * right that the pickers' own named copy should reach the user; the mechanism
 * took every `Error` in reach. This type is the difference, and the panel
 * shows the message ONLY for one of these.
 *
 * `stagedFile.ts` rather than the picker, because both platform siblings and
 * the panel need it and this file has no siblings to shadow it.
 */
export class PickerError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'PickerError';
    // `extends Error` across the TS downlevel target loses the prototype, and
    // `instanceof` is the whole point of this class.
    Object.setPrototypeOf(this, PickerError.prototype);
  }
}

export const keepReadableFiles = (
  results: PromiseSettledResult<StagedFile>[],
  chosenNames: string[],
): { staged: StagedFile[]; error: string | null } => {
  const staged: StagedFile[] = [];
  const lost: string[] = [];

  results.forEach((result, i) => {
    if (result.status === 'fulfilled') {
      staged.push(result.value);
      return;
    }
    lost.push(chosenNames[i] || 'one file');
  });

  if (lost.length === 0) return { staged, error: null };

  // Nothing survived: the batch failed, and naming one file is friendlier than
  // naming six.
  if (staged.length === 0) {
    return {
      staged,
      error:
        lost.length === 1
          ? `Could not read ${lost[0]}.`
          : 'Could not read any of those files.',
    };
  }

  // Some survived. Name what did NOT, because the strip is about to show what
  // did and the difference is the whole message.
  return {
    staged,
    error:
      lost.length === 1
        ? `Could not read ${lost[0]}. The rest were attached.`
        : `Could not read ${lost.join(', ')}. The rest were attached.`,
  };
};
